// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { injectIntl, type IntlShape } from 'react-intl';
import {
  Field,
  formValueSelector,
  reduxForm,
  type FormProps,
} from 'redux-form';

import type {
  Dispatch,
  MetadataDefinition,
  NamespaceDefinition,
} from 'state/types';
import { bindActionCreatorsSafe } from 'util/safeDispatch';
import {
  CREATE_METADATA_PANEL_ID,
  CREATE_METADATA_FORM_ID,
} from 'util/constants';
import localizedStrings from 'util/localizedStrings';
import DialogPanel from 'view/App/DialogPanels/DialogPanel';
import {
  getActivePanelId,
  getCustomXMPNamespaces,
  getMetadataDefinitions,
} from 'state/selectors';
import { actions as dialogActions } from 'portfolio-shared/state/reducers/dialogPanelReducer';
import {
  createMetadataDefinitions,
  getCustomXmpNamespaces as getCustomXmpNamespacesAction,
} from 'state/commands/catalogCommands';
import LabeledField from 'view/Forms/LabeledField';
import {
  FIELDS_CATEGORY_XMP,
  getMetadataNamespaces,
  MD_DATATYPES,
  MD_TYPES,
  MD_MULTIVALUED_TYPES,
} from 'util/fieldsPageUtils';

const { hideDialogPanel } = dialogActions;

type Props = {
  activePanelId: ?string,
  createMetadataDefinitions: Function,
  customXMPNamespaces: NamespaceDefinition[],
  hideDialogPanel: Function,
  intl: IntlShape,
  metadataDefinitions: MetadataDefinition[],
  selectedPath: ?string,
} & FormProps;

const initialFieldState: MetadataDefinition = {
  canEmbed: true,
  dataType: 'String',
  displayName: '',
  displayNames: [],
  id: '',
  isEditable: true,
  multivalueType: 'NotMultiValued',
  namespace: 'dc',
  parentId: '',
  tag: '',
  type: FIELDS_CATEGORY_XMP,
};

class CreateMetadataDefinitionDialog extends Component<Props> {
  submit: Function;
  isOkEnabled: Function;

  constructor() {
    super();
    this.isOkEnabled = this.isOkEnabled.bind(this);
    this.submit = this.submit.bind(this);
  }

  componentDidMount() {
    this.props.getCustomXmpNamespacesAction();
  }

  async submit(values) {
    const { createMetadataDefinitions } = this.props;
    const metadataDefinition = { ...values };
    // special case for tag
    const tagPrefix =
      values.type === FIELDS_CATEGORY_XMP ? `${values.namespace}:` : '';
    metadataDefinition.tag = `${tagPrefix}${values.tag}`;
    await createMetadataDefinitions([metadataDefinition]);
  }

  isOkEnabled() {
    return this.props.valid;
  }

  render() {
    const {
      activePanelId,
      customXMPNamespaces,
      handleSubmit,
      hideDialogPanel,
      intl: { formatMessage },
      metadataDefinitions,
      namespace,
      type,
    } = this.props;
    const namespaces: string[] = getMetadataNamespaces(
      customXMPNamespaces,
      metadataDefinitions
    );
    return (
      <DialogPanel
        activePanelId={activePanelId}
        contentStyle={{ width: '650px', height: null }}
        customButtonText={formatMessage(localizedStrings.CREATE)}
        dialogFocusId="displayName"
        hideDialogPanel={hideDialogPanel}
        isOkEnabled={this.isOkEnabled}
        onClose={() => {}}
        onSave={handleSubmit(this.submit)}
        panelId={CREATE_METADATA_PANEL_ID}
        panelTitle="Create Metadata Definition"
        showCancel
        dataTestName="create-metadata-dialog"
      >
        <form
          className="portfolio-form-container"
          onSubmit={handleSubmit(this.submit)}
        >
          <LabeledField
            label={formatMessage(localizedStrings.FIELD_METADATA_NAME_LABEL)}
          >
            <Field
              className="input"
              component="input"
              id="displayName"
              name="displayName"
              type="text"
            />
          </LabeledField>
          <div className="field is-horizontal">
            <div className="field-label is-normal">
              <div className="label">
                {formatMessage(localizedStrings.FIELD_METADATA_TYPE_LABEL)}
              </div>
            </div>
            <div className="field-body">
              <div className="field">
                <div className="control is-expanded">
                  <div className="select is-fullwidth">
                    <Field
                      className="select"
                      component="select"
                      id="type"
                      name="type"
                    >
                      {MD_TYPES.map(type => (
                        <option key={type.id} value={type.id}>
                          {formatMessage(type.label)}
                        </option>
                      ))}
                    </Field>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {type === FIELDS_CATEGORY_XMP && (
            <div className="field is-horizontal">
              <div className="field-label is-normal">
                <div className="label">
                  {formatMessage(
                    localizedStrings.FIELD_METADATA_NAMESPACE_LABEL
                  )}
                </div>
              </div>
              <div className="field-body">
                <div className="field">
                  <div className="control is-expanded">
                    <div className="select is-fullwidth">
                      <Field
                        className="select"
                        component="select"
                        id="namespace"
                        name="namespace"
                      >
                        {namespaces.map(ns => (
                          <option key={ns} value={ns}>
                            {ns}
                          </option>
                        ))}
                      </Field>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="field is-horizontal has-addons">
            <div className="field-label is-normal">
              <div className="label">
                {formatMessage(localizedStrings.FIELD_METADATA_TAG_LABEL)}
              </div>
            </div>
            <div className="field-body">
              <div className="field has-addons">
                {type === FIELDS_CATEGORY_XMP && (
                  <div className="control">
                    <a className="button is-static">{namespace}:</a>
                  </div>
                )}
                <div className="control is-expanded">
                  <Field
                    className="input"
                    component="input"
                    id="tag"
                    name="tag"
                    type="text"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="field is-horizontal">
            <div className="field-label is-normal">
              <div className="label">
                {formatMessage(localizedStrings.FIELD_METADATA_DATA_TYPE_LABEL)}
              </div>
            </div>
            <div className="field-body">
              <div className="field">
                <div className="control is-expanded">
                  <div className="select is-fullwidth">
                    <Field
                      className="select"
                      component="select"
                      id="dataType"
                      name="dataType"
                    >
                      {MD_DATATYPES.map(type => (
                        <option key={type.id} value={type.id}>
                          {formatMessage(type.label)}
                        </option>
                      ))}
                    </Field>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="field is-horizontal">
            <div className="field-label is-normal">
              <div className="label">
                {formatMessage(
                  localizedStrings.FIELD_METADATA_MULTIVALUED_TYPE_LABEL
                )}
              </div>
            </div>
            <div className="field-body">
              <div className="field">
                <div className="control is-expanded">
                  <div className="select is-fullwidth">
                    <Field
                      className="select"
                      component="select"
                      id="multivalueType"
                      name="multivalueType"
                    >
                      {MD_MULTIVALUED_TYPES.map(type => (
                        <option key={type.id} value={type.id}>
                          {formatMessage(type.label)}
                        </option>
                      ))}
                    </Field>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </DialogPanel>
    );
  }
}

function validate(data: Object, props: Props) {
  const errors = {};
  if (!data.displayName) {
    errors.displayName = 'Required';
  }
  if (!data.tag) {
    errors.tag = 'Required';
  }
  return errors;
}

const form = reduxForm({
  form: CREATE_METADATA_FORM_ID,
  initialValues: initialFieldState,
  validate,
})(CreateMetadataDefinitionDialog);

const selector = formValueSelector(CREATE_METADATA_FORM_ID);

export default connect(
  state => ({
    activePanelId: getActivePanelId(state),
    customXMPNamespaces: getCustomXMPNamespaces(state),
    metadataDefinitions: getMetadataDefinitions(state),
    namespace: selector(state, 'namespace'),
    type: selector(state, 'type'),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      createMetadataDefinitions,
      getCustomXmpNamespacesAction,
      hideDialogPanel,
    })
)(injectIntl(form));



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/CreateMetadataDefinitionDialog.js